import React from 'react';
import PropTypes from 'prop-types';
import pencilIcon from '../../../../../Assets/images/svg/dar_pencil.svg';
import deleteIcon from '../../../../../Assets/images/svg/dar_delete.svg';

const STATUS_RING_COLORS = {
  open: '#6fd3e6',
  closed: '#ef5350',
  escalated: '#9a92f7',
};
const EMPTY_RING_COLOR = '#dfe3eb';
const TOTAL_RING_SIZE = 84;
const TOTAL_RING_STROKE_WIDTH = 8;
const TOTAL_RING_RADIUS = (TOTAL_RING_SIZE - TOTAL_RING_STROKE_WIDTH) / 2;
const TOTAL_RING_GAP_ANGLE = 14;

export const normalizeStatusClass = (value = '') => String(value)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const formatIndianCurrency = (value) => new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

export const DateCell = ({ date, day }) => (
  <div className="date-cell">
    <div className="date-main">{date || ''}</div>
    <div className="date-day">{day || ''}</div>
  </div>
);

export const ActionButtons = ({ onEdit, onDelete }) => {
  const showEdit = typeof onEdit === 'function';
  const showDelete = typeof onDelete === 'function';

  if (!showEdit && !showDelete) return null;

  return (
    <div className="action-btn-group">
      {showEdit ? (
        <button type="button" className="action-btn" aria-label="Edit" onClick={onEdit}>
          <img src={pencilIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
      {showDelete ? (
        <button type="button" className="action-btn" aria-label="Delete" onClick={onDelete}>
          <img src={deleteIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export const SummaryRing = ({ segments }) => {
  const center = TOTAL_RING_SIZE / 2;
  const circumference = 2 * Math.PI * TOTAL_RING_RADIUS;
  const visibleSegments = Array.isArray(segments)
    ? segments.filter((segment) => segment.value > 0)
    : [];
  const total = visibleSegments.reduce((sum, segment) => sum + segment.value, 0);

  if (!visibleSegments.length || total <= 0) {
    return (
      <svg
        className="total-ring-chart"
        width={TOTAL_RING_SIZE}
        height={TOTAL_RING_SIZE}
        viewBox={`0 0 ${TOTAL_RING_SIZE} ${TOTAL_RING_SIZE}`}
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={TOTAL_RING_RADIUS}
          fill="none"
          stroke={EMPTY_RING_COLOR}
          strokeWidth={TOTAL_RING_STROKE_WIDTH}
        />
      </svg>
    );
  }

  const segmentCount = visibleSegments.length;
  const gapAngle = segmentCount > 1 ? TOTAL_RING_GAP_ANGLE : 0;
  const totalGapAngle = gapAngle * segmentCount;
  const availableAngle = Math.max(0, 360 - totalGapAngle);
  let currentAngle = segmentCount > 1 ? gapAngle / 2 : 0;

  return (
    <svg
      className="total-ring-chart"
      width={TOTAL_RING_SIZE}
      height={TOTAL_RING_SIZE}
      viewBox={`0 0 ${TOTAL_RING_SIZE} ${TOTAL_RING_SIZE}`}
      aria-hidden="true"
    >
      {visibleSegments.map((segment, index) => {
        const angle = total > 0 ? (segment.value / total) * availableAngle : 360;
        const dashLength = (angle / 360) * circumference;
        const dashOffset = -((currentAngle / 360) * circumference);

        currentAngle += angle;
        if (index < segmentCount - 1) currentAngle += gapAngle;

        return (
          <circle
            key={segment.id || `${segment.color}-${segment.value}-${index}`}
            cx={center}
            cy={center}
            r={TOTAL_RING_RADIUS}
            fill="none"
            stroke={segment.color || STATUS_RING_COLORS.open}
            strokeWidth={TOTAL_RING_STROKE_WIDTH}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            className="total-ring-segment"
            style={{
              '--ring-dasharray': `${dashLength}px ${Math.max(circumference - dashLength, 0)}px`,
              '--ring-dashoffset': `${dashOffset}px`,
              '--ring-dashoffset-start': `${dashOffset - (12 + (index * 6))}px`,
            }}
          />
        );
      })}
    </svg>
  );
};

export const EmptyState = ({ text = 'No data to display' }) => (
  <div className="invoice-empty-state">
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <p>{text}</p>
  </div>
);

DateCell.propTypes = {
  date: PropTypes.string,
  day: PropTypes.string,
};

ActionButtons.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

SummaryRing.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  })),
};

EmptyState.propTypes = {
  text: PropTypes.string,
};
